// Program Settings › Google Drive. Words: SITE_FOLDERS_WORDS.md rev 3.1 section 13. Mockup: MOCKUP_DRIVE_FOLDERS.html rev 2 panel D.
// Admin covering all sites. Connect opens Google's sign-in; Google sends the browser back to
// #/settings/drive?drive=connected (or failed&why=...). Change and Disconnect run in server code (calls 93, 94).

import { callServer, readDriveSettings, withTimeLimit } from "./firebase";
import type { DriveSettings } from "./drive-folders";
import { driveSettingsPage, type DriveSettingsView } from "./drive-screens";

let view: DriveSettingsView = { settings: null, busy: "", error: "", changing: false, confirming: false, back: null };
let loadedFor = "";
let readFailed = false;

// Back from Google's page without finishing: Connect is a button again, not "Opening Google…" for good.
if (typeof window !== "undefined") {
  const unstick = () => {
    if (document.visibilityState !== "visible" || view.busy !== "connect") return;
    view = { ...view, busy: "" };
    // The Home Screen app comes back after Google's window closes: read Drive again to show Connected.
    loadedFor = "";
    // Redraw the page through the app's own route change.
    if (location.hash.startsWith("#/settings/drive")) window.dispatchEvent(new HashChangeEvent("hashchange"));
  };
  window.addEventListener("pageshow", unstick);
  document.addEventListener("visibilitychange", unstick);
}

/** The iPad Home Screen app (no Safari bar). Google's sign-in then opens in a window over it. */
function homeScreenApp(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)").matches === true || (navigator as { standalone?: boolean }).standalone === true;
}

/** What the sign-in brought back in the address. Read once, then the address is cleaned. */
export function takeConnectResult(hashPart: string): void {
  const q = hashPart.split("?")[1];
  if (!q) return;
  const p = new URLSearchParams(q);
  const result = p.get("drive");
  if (result === "connected" || result === "failed") view = { ...view, back: { result, why: p.get("why") ?? "" }, busy: "" };
  location.replace("#/settings/drive");
}

/** The page. Reads settings/drive the first time for this company, then on every change. */
export function driveSettingsScreen(company: string, rerender: () => void): string {
  if (loadedFor !== company) {
    loadedFor = company;
    void reload(company, rerender);
  }
  return driveSettingsPage({ ...view, retry: readFailed });
}

async function reload(company: string, rerender: () => void): Promise<void> {
  try {
    view = { ...view, settings: (await withTimeLimit(readDriveSettings(company))) as Partial<DriveSettings> | null, error: readFailed ? "" : view.error };
    readFailed = false;
  } catch {
    // Not "Not connected": the read failed. Say so and offer Try Again.
    readFailed = true;
    view = { ...view, error: "Could not read the Google Drive settings. Check your signal and tap Try Again." };
  }
  rerender();
}

/** Clicks on [data-dv] inside the page. Returns true when it handled one. */
export function onDriveSettingsClick(target: HTMLElement, company: string, rerender: () => void): boolean {
  const el = target.closest<HTMLElement>("[data-dv]");
  if (!el || (el as HTMLButtonElement).disabled) return false;
  const act = el.dataset.dv;
  const set = (v: Partial<DriveSettingsView>) => {
    view = { ...view, ...v };
    rerender();
  };
  switch (act) {
    case "reload":
      set({ error: "" });
      void reload(company, rerender);
      return true;
    case "connect":
      set({ busy: "connect", error: "", back: null });
      void callServer<{ url: string }>("startDriveConnect", { returnTo: location.origin, homeScreenApp: homeScreenApp() })
        .then(({ url }) => location.assign(url))
        .catch((e: unknown) => set({ busy: "", error: e instanceof Error ? e.message : String(e) }));
      return true;
    case "change":
      set({ changing: true, confirming: false, error: "", back: null });
      return true;
    case "change-cancel":
      set({ changing: false, error: "" });
      return true;
    case "change-save": {
      const folder = (document.getElementById("dv-folder") as HTMLInputElement | null)?.value ?? "";
      set({ busy: "change", error: "" });
      void callServer<{ rootFolderId: string; rootFolderName: string }>("setDriveFolder", { folder })
        .then(() => {
          view = { ...view, busy: "", changing: false };
          return reload(company, rerender);
        })
        .catch((e: unknown) => set({ busy: "", error: e instanceof Error ? e.message : String(e) }));
      return true;
    }
    case "disconnect":
      set({ confirming: true, changing: false, error: "", back: null });
      return true;
    case "disconnect-cancel":
      set({ confirming: false, error: "" });
      return true;
    case "disconnect-yes":
      set({ busy: "disconnect", error: "" });
      void callServer("disconnectDrive", { confirmed: true })
        .then(() => {
          view = { ...view, busy: "", confirming: false };
          return reload(company, rerender);
        })
        .catch((e: unknown) => set({ busy: "", error: e instanceof Error ? e.message : String(e) }));
      return true;
    default:
      return false;
  }
}
